"use client";

import {
  MeiliOrigin,
  MindstringPrototype,
  Origin,
  SupportedLanguage,
} from "@/generated/fastapi";
import { useState } from "react";
import { VStack } from "@chakra-ui/react";
import {
  useSituationAccessors,
  useSituationsQuery,
} from "@/features/situations";
import { OriginSubmitForm } from "@/features/origins";
import { turnOriginsToMeiliOrigins } from "@/core/search";
import { mindstringPrototypesSlang } from "@/shared/config/backend";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_MESSAGES,
} from "@/features/profile-shaping";
import SourceExtractionWorkflow from "../source-extraction/SourceExtractionWorkflow";

interface SituationSourceShapingEntryProps {
  situationId: string;
  prototype: MindstringPrototype;
  onCancel?: () => void;
  showOrientation?: boolean;
}

export default function SituationSourceShapingEntry({
  situationId,
  prototype,
  onCancel,
  showOrientation = true,
}: SituationSourceShapingEntryProps) {
  const situationsQuery = useSituationsQuery();
  const { getSituationById } = useSituationAccessors(situationsQuery.data);
  const presetSituation = getSituationById(situationId);
  const [createdOriginForMeili, setCreatedOriginForMeili] =
    useState<MeiliOrigin | null>(null);
  const isExtractionPhase = Boolean(createdOriginForMeili);
  const prototypeLabel =
    mindstringPrototypesSlang[prototype]?.label ?? prototype;
  const situationContextLabel = [
    presetSituation?.position,
    presetSituation?.context,
  ]
    .filter(Boolean)
    .join(", ");
  const situationLabel =
    presetSituation?.abbr || situationContextLabel || "Chosen situation";
  const phaseLabel = isExtractionPhase
    ? PROFILE_SHAPING_LABELS.extractionPhase
    : PROFILE_SHAPING_LABELS.originCreationPhase;
  const description = isExtractionPhase
    ? PROFILE_SHAPING_MESSAGES.shapeFromSource
    : "Store source material in Backlog first. The next step will shape it into suggested thoughts anchored to this situation.";

  const handleOriginSuccess = (origin: Origin) => {
    setCreatedOriginForMeili(turnOriginsToMeiliOrigins([origin])[0]);
  };

  return (
    <VStack gap="4" align="stretch">
      {showOrientation ? (
        <GenericDrawerInfo
          description={description}
          items={[
            { label: phaseLabel, colorPalette: "blue" },
            { label: situationLabel, colorPalette: "gray" },
            { label: prototypeLabel, colorPalette: "purple" },
          ]}
        />
      ) : null}

      {createdOriginForMeili ? (
        <SourceExtractionWorkflow
          allowUnanchoredExtraction={false}
          meiliOrigin={createdOriginForMeili}
          presetSituation={presetSituation}
          presetPrototype={prototype}
          presetLanguage={
            (createdOriginForMeili.language as SupportedLanguage) || undefined
          }
          showOrientation={showOrientation}
          onCancel={onCancel}
          showReturnHeader={showOrientation}
        />
      ) : (
        <OriginSubmitForm
          onSuccess={handleOriginSuccess}
          submitButtonText={PROFILE_SHAPING_LABELS.originCreationPhase}
          resetFormOnSuccess={false} // Don't reset since we're moving to next step
          sourcedWhere="backlog"
          showOrientation={showOrientation}
          allowExistingSourceSelection
          onExistingSourceSelect={(origin) => {
            setCreatedOriginForMeili(origin);
          }}
        />
      )}
    </VStack>
  );
}
